/* eslint no-underscore-dangle: 0 */
import React, { useCallback, useEffect, useState } from 'react'
import { compose, withStateHandlers } from 'recompose'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import { withAuth } from '../../components/auth'
import ErrorBoundary from '../../components/errorBoundary'
import features from '../../components/features'
import settings from '../../components/settings'
import { Container as ContainerUI } from '../../components/grid'
import withHelmet from '../../components/with-helmet/withHelmet'
import Link from '../../components/typography/link'
import Preamble from '../../components/typography/preamble'
import PageTitle from '../../components/typography/pageTitle'
import tracker from '../../../utils/tracker'
import { badaDefaultProps } from '../../../types'
import { clearState, saveState } from '../../../utils/state'
import Alert from '../../components/alert'
import ContentHeader from '../../components/content-header'
import Header from '../../components/header'
import Loader from '../../components/loader/Loader'
import { fetchStores } from './actions'
import ConfirmationPage from './pages/ConfirmationPage'
import LandingPage from './pages/landingPage/LandingPage'
import PrepFormPage from './pages/PrepFormPage'
import stateReducer from './reducer'
import { isEmbedded } from './utils'
import { brand } from '../../../../config/baseSettings'
import useTranslation from '../../../utils/translation'
import { appBaseUrl } from '../../../utils/appBaseUrl'
import { useHookAsHoc } from '../../hooks/useHookAsHoc'
import { useCms } from '../../components/cms/CmsContext'
import { useMyProjectCustomer } from '../../hooks/customer/useMyProjectCustomer'
import useServiceProviderById from '../../hooks/service-provider/useServiceProviderById'
import {
  queryCustomerId,
  queryFlow,
  queryMeetingTypeId,
  queryStudioId,
} from '../../../utils/initValues'
import { useGetAppointment } from '../../../utils/badaAppointmentApi'

const Container = styled(ContainerUI)`
  width: 100%;
  padding-bottom: 300px;
  max-width: ${theme('grid.containerMaxWidth')};
  display: flex;
  flex-direction: column;
`

const StyledContainer = styled.div`
  margin: 30px 0px 30px 0px;
  text-align: center;
  ${(props) => props.theme.media.min.sm`
    margin: 60px 0px 30px 0px;
  `}
`

const ErrorTitle = styled(PageTitle)`
  color: #dc4d4d;
`

const Bada = (props) => {
  const [currentHash, setCurrentHash] = useState(null)
  const embedded = isEmbedded()
  const t = useTranslation('app')

  useEffect(() => {
    const { history, updateUiState, visitorId, appointmentId, flow } = props

    if (appointmentId) {
      clearState(brand)
      updateUiState({
        returningVisitor: true,
        appointmentFromQuery: true,
        appointmentId,
        bookingCompleted: true,
        prepCompleted: false,
        booking: {
          bookingCompleted: true,
          loading: false,
        },
      })
    }

    if (visitorId) updateUiState({ visitorId })

    window.zeus = window.zeus || {}
    window.zeus.bada_api = {
      setState: updateUiState,
    }

    history.listen((newLocation, action) => {
      if (action === 'POP') {
        if (currentHash === '#details-submit' || currentHash === '#complete') {
          history.goForward()
        }
      } else if (action === 'PUSH') {
        setCurrentHash(newLocation.hash)
      }
    })

    tracker.trackInitVisitor('BADA 2.0 init visitor', visitorId, flow)

    return () => delete window.zeus.bada_api
  }, [])

  const needsCustomerData = props.flow === 'followup' || !!queryCustomerId
  const [customerDataLoaded, setCustomerDataLoaded] = useState(
    !needsCustomerData
  )

  useEffect(() => {
    const { updateUiState } = props
    if (needsCustomerData) {
      useMyProjectCustomer()
        .then((customer) => {
          // Extract preparation questions from customer's latest appointment
          const previousQuestions =
            customer.appointmentPreparations?.questions || []

          updateUiState({
            storeId: customer.storeId,
            selectedStore: null,
            selectedServiceProvider: null,
            selectedService: null,
            selectedSlot: null,
            preselected: true,
            previousQuestions,
            values: {
              name: customer.name,
              email: customer.email,
              phone: customer.phone?.replaceAll(' ', ''),
              address: customer.address1,
              address2: customer.address2,
              address3: customer.address3,
              city: customer.city,
              country: customer.country,
              zipcode: customer.zipcode,
            },
            selectedServiceProviderId: customer.serviceProviderId,
          })
        })
        .finally(() => {
          setCustomerDataLoaded(true)
        })
    } else {
      setCustomerDataLoaded(true)
    }
  }, [needsCustomerData])

  const { minimalHeader, showAlert } = features
  const {
    bookingCompleted,
    wizard: { loading },
    booking,
  } = props

  const hasError = booking && !!booking.error

  const { data: selectedServiceProvider, isLoading: loadingFlow } =
    useServiceProviderById(
      props.selectedStore?.id,
      props.selectedStore?.simplyBookMeSubsystem,
      props.selectedServiceProviderId
    )

  useEffect(() => {
    if (selectedServiceProvider) {
      props.updateUiState({ selectedServiceProvider })
    }
  }, [selectedServiceProvider, props.updateUiState])

  const content = useCallback(
    () => (
      <>
        {showAlert && <Alert wizard={props.wizard} />}
        {isEmbedded(true) === false && (
          <LandingPage props={{ ...props, customerDataLoaded }} />
        )}
        <PrepFormPage props={props} />
        <ConfirmationPage props={props} />
        {(loading || loadingFlow || !customerDataLoaded) && (
          <Loader duration={3} text={t('loader.text.content')} />
        )}
      </>
    ),
    [props, loading, loadingFlow, customerDataLoaded]
  )

  const error = useCallback(
    () => (
      <StyledContainer>
        <ErrorTitle>{t('error.title')}</ErrorTitle>
        <Preamble>
          <Link href={`${appBaseUrl}`}>Please try again &raquo;</Link>
        </Preamble>
      </StyledContainer>
    ),
    [t]
  )

  return (
    <>
      <ErrorBoundary>
        {minimalHeader && !embedded && <Header />}
        {isEmbedded(true) === false && (
          <ContentHeader show={!bookingCompleted} />
        )}
        <Container data-track="container">
          {hasError ? error() : content()}
        </Container>
      </ErrorBoundary>
    </>
  )
}

Bada.defaultProps = {
  ...badaDefaultProps,
}

const enhance = compose(
  withHelmet(() => ({
    url: `${appBaseUrl}`,
  })),
  withStateHandlers(
    (props) => ({
      storeId: null,
      selectedAddress: null,
      selectedStore: null,
      selectedService: null,
      selectedSlot: null,
      selectedServiceProviderEmail: null,
      selectedServiceProvider: null,
      booking: null,
      bookingCompleted: false,
      customerForm: null,
      prepCompleted: false,
      flow: queryFlow,
      prepData: {
        questions: [],
      },
      appointmentId: null,
      visitorId: null,
      ...props,
      fallbackAddress: features.disableFallbackLocation
        ? null
        : {
            city: settings.fallbackAddress.city,
            countryName: settings.fallbackAddress.countryName,
            postalCode: settings.fallbackAddress.postalCode,
            latitude: settings.fallbackAddress.latitude,
            longitude: settings.fallbackAddress.longitude,
          },
      ...(queryStudioId
        ? {
            storeId: queryStudioId,
            selectedService: null,
            selectedSlot: null,
          }
        : {}),
      ...(queryMeetingTypeId
        ? {
            preSelectedMeetingTypeId: queryMeetingTypeId,
          }
        : {}),
      ...((queryFlow || queryCustomerId)
        ? {
            storeId: null,
            selectedStore: null,
            selectedService: null,
            selectedSlot: null,
            selectedServiceProvider: null,
            selectedServiceProviderEmail: null,
            preselected: false,
          }
        : {}),
    }),
    {
      updateUiState: (props) => (newProp) => {
        const state = { ...props, ...newProp }
        setTimeout(() => saveState(state, settings.brand), 100)
        return state
      },
    }
  ),
  useHookAsHoc('pageContentServices', useCms, ({ services }) => services),
  fetchStores,
  useHookAsHoc(
    'appointment',
    ({ appointmentId }) => useGetAppointment(appointmentId),
    ({ data, isLoading, ...result }) => ({
      ...result,
      loading: isLoading,
      appointment: data,
    })
  ),
  stateReducer,
  withAuth
)

export default enhance(withRouter(Bada))
